import type { ReactElement } from "react";

import { Btn2 } from "@/components/hifi/Btn2";
import { Card } from "@/components/hifi/Card";
import { Icon, type IconName } from "@/components/hifi/Icon";
import { Pill } from "@/components/hifi/Pill";
import { ShellLayout } from "@/components/shell/ShellLayout";

type Template = {
  key: string;
  title: string;
  blurb: string;
  icon: IconName;
  accent: string;
  status: "planned" | "beta";
};

const TEMPLATES: Template[] = [
  {
    key: "sra",
    title: "Full SRA Report",
    blurb:
      "Security Risk Assessment — full narrative: scope, asset inventory, threat set, likelihood, impact, IRV, treatment, and residual risk.",
    icon: "file",
    accent: "var(--a-600)",
    status: "planned",
  },
  {
    key: "dbt",
    title: "DBT Summary",
    blurb:
      "Design Basis Threat summary — adversary profiles, attack scenarios, capability ratings, and defence posture.",
    icon: "target",
    accent: "var(--warn)",
    status: "planned",
  },
  {
    key: "action",
    title: "Action Plan",
    blurb:
      "Mitigation tracker — open tasks grouped by owner and priority, with SLA and overdue flags.",
    icon: "checkBox",
    accent: "var(--ok)",
    status: "planned",
  },
  {
    key: "incidents",
    title: "Incident Log",
    blurb:
      "Time-ordered incident register with severity, status, review outcomes, and linked assessments.",
    icon: "alert",
    accent: "var(--bad)",
    status: "planned",
  },
  {
    key: "audit",
    title: "Audit Trail",
    blurb:
      "Immutable audit log export — who did what, when, on which record. Scoped by date range and actor.",
    icon: "history",
    accent: "var(--n-700)",
    status: "planned",
  },
  {
    key: "compliance",
    title: "Compliance Coverage",
    blurb:
      "Framework coverage report — ISO 31000, NIS2, CER, ASIS — mapped to assessments, threats, and controls.",
    icon: "shield",
    accent: "var(--a-700)",
    status: "planned",
  },
];

export function ReportsPage(): ReactElement {
  return (
    <ShellLayout
      active="reports"
      topbar={{
        crumbs: ["Workspace", "Reports"],
        title: "Reports",
        subtitle: "Six export templates planned for Phase J",
      }}
    >
      <div className="space-y-5 p-5">
        <Card>
          <div className="flex items-start gap-3 px-4 py-3">
            <div
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-2"
              style={{ background: "var(--a-50)" }}
            >
              <Icon name="sparkle" size={16} color="var(--a-600)" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-[13px] font-semibold text-n-900">
                  Report generation
                </div>
                <Pill variant="accent">Planned</Pill>
              </div>
              <p className="mt-1 text-[12px] text-n-500">
                PDF export pipeline (Playwright → PDF or{" "}
                <code className="font-mono text-[11px]">@react-pdf/renderer</code>)
                is on the Phase J roadmap. Templates below preview the final catalogue.
              </p>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {TEMPLATES.map((t) => (
            <Card key={t.key} accent={t.accent}>
              <div className="p-4">
                <div className="flex items-center gap-2">
                  <div
                    className="flex h-8 w-8 items-center justify-center rounded-2"
                    style={{ background: "var(--n-75)" }}
                  >
                    <Icon name={t.icon} size={15} color={t.accent} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-n-900">
                      {t.title}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3px] text-n-500">
                      PDF export
                    </div>
                  </div>
                  <Pill variant={t.status === "beta" ? "warn" : "default"}>
                    {t.status === "beta" ? "Beta" : "Planned"}
                  </Pill>
                </div>
                <p className="mt-3 text-[12px] leading-[1.5] text-n-700">
                  {t.blurb}
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <Btn2 size="sm" icon="download" disabled>
                    Generate
                  </Btn2>
                  <span className="text-[11px] text-n-400">
                    Not available yet
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </ShellLayout>
  );
}
